import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  getExhibitionSettings,
  getLoginMode,
  getProfile,
  getSelectedExhibitionId,
  getSelectedStep,
  selectInitializeConteoLogin,
  selectIsFirstLoadDone,
  selectIsLogoutProcessing,
  selectLatestInitializedConteoLoginExhibitionId,
  selectLoginType,
  selectReinitializeConteoLogin
} from '@app/app.reducer';
import { Store } from '@ngrx/store';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { environment } from '@src/environments/environment';
import {
  SetInitializeConteoLogin,
  SetLatestInitializedConteoLoginExhibitionId,
  SetReinitializeConteoLogin
} from '@store/helpers/helper.actions';
import { HelperService } from '@store/helpers/helper.service';
import { selectAreProductSelectionProductsLoaded } from '@store/products/product-selection/product-selection.selectors';
import { StepName } from '@store/step-forms/step.interface';
import { LoginType } from '@store/user/user.interface';
import { UserService } from '@store/user/user.service';
import { isEmpty, isNil } from 'lodash';
import { combineLatest, filter, first, map, Observable, pairwise, withLatestFrom } from 'rxjs';
import {
  LoginOptions,
  SocialLoginQueryParams,
  SocialLoginQueryParamsFirstLogin,
  SocialLoginQueryParamsLoginType
} from './login.model';
import { LoginService } from './login.service';

@Injectable()
export class ConteoLoginService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly loginService = inject(LoginService);
  private readonly userService = inject(UserService);
  private readonly statusBarService = inject(StatusBarService);
  private readonly helperService = inject(HelperService);

  private isFirstLoadDone$ = this.store.select(selectIsFirstLoadDone);
  private profile$ = this.store.select(getProfile);
  private exhibitionSettings$ = this.store.select(getExhibitionSettings);
  private selectedExhibitionId$ = this.store.select(getSelectedExhibitionId);
  private isLogoutProcessing$ = this.store.select(selectIsLogoutProcessing);
  private initializeConteoLogin$ = this.store.select(selectInitializeConteoLogin);
  private latestInitializedConteoLoginExhibitionId$ = this.store.select(selectLatestInitializedConteoLoginExhibitionId);
  private isSelectExhibitionPage$ = this.helperService.isSelectExhibitionPage$();
  private isLoginModeBeforeTicketSelection$ = this.store
    .select(getLoginMode)
    .pipe(map((loginMode) => loginMode === LoginOptions.BeforeTicketSelection));
  private isTicketOrLegitimationSelectedStep$ = this.store
    .select(getSelectedStep)
    .pipe(map((selectedStep) => selectedStep === StepName.Tickets || selectedStep === StepName.Legitimation));

  initConteoLoginServiceSubscriptions() {
    this.initializeConteoLoginSubscription();
    this.initializeConteoLoginAndLogoutSubscription();
    this.conteoLoginQueryParamsSubscription();

    this.setReinitializeConteoLoginOnLogoutProcessingSubscription();
    this.reinitializeConteoLoginOnPageReloadSubscription();

    this.resetConteoLoginOnEventSelectionPageSubscription();
    this.resetConteoLoginAfterExhibitionSelectLoginSubscription();
    this.resetConteoLoginOnSelectSameExhibitionWithoutLoggedInConteoUserSubscription();
    this.resetConteoLoginInitializeAndReinitializeAfterUserLoginSubscription();
  }

  private initializeConteoLoginSubscription() {
    return combineLatest({
      exhibitionSettings: this.exhibitionSettings$,
      isLoginModeBeforeTicketSelection: this.isLoginModeBeforeTicketSelection$,
      isTicketOrLegitimationSelectedStep: this.isTicketOrLegitimationSelectedStep$,
      isLogoutProcessing: this.isLogoutProcessing$,
      latestInitializedConteoLoginExhibitionId: this.latestInitializedConteoLoginExhibitionId$
    })
      .pipe(
        first(
          ({
            exhibitionSettings,
            isLoginModeBeforeTicketSelection,
            isTicketOrLegitimationSelectedStep,
            isLogoutProcessing,
            latestInitializedConteoLoginExhibitionId
          }) =>
            exhibitionSettings?.initiateConteoLogin &&
            isLoginModeBeforeTicketSelection &&
            isTicketOrLegitimationSelectedStep &&
            !isLogoutProcessing &&
            isNil(latestInitializedConteoLoginExhibitionId)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.store.dispatch(new SetInitializeConteoLogin(true)));
  }

  private initializeConteoLoginAndLogoutSubscription() {
    return combineLatest({
      profile: this.profile$,
      exhibitionSettings: this.exhibitionSettings$,
      isLogoutProcessing: this.isLogoutProcessing$,
      initializeConteoLogin: this.initializeConteoLogin$,
      latestInitializedConteoLoginExhibitionId: this.latestInitializedConteoLoginExhibitionId$,
      areProductSelectionProductsLoaded: this.store.select(selectAreProductSelectionProductsLoaded)
    })
      .pipe(
        first(
          ({
            exhibitionSettings,
            isLogoutProcessing,
            initializeConteoLogin,
            latestInitializedConteoLoginExhibitionId,
            areProductSelectionProductsLoaded
          }) =>
            exhibitionSettings?.initiateConteoLogin &&
            !isLogoutProcessing &&
            initializeConteoLogin &&
            isNil(latestInitializedConteoLoginExhibitionId) &&
            areProductSelectionProductsLoaded
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ profile, exhibitionSettings }) => {
        if (!isNil(profile)) {
          this.userService.logout();
        } else {
          this.conteoLogin(exhibitionSettings.eventId);
        }
      });
  }

  private conteoLoginQueryParamsSubscription() {
    return combineLatest({
      queryParams: this.activatedRoute.queryParams as Observable<SocialLoginQueryParams>,
      isSelectExhibitionPage: this.isSelectExhibitionPage$,
      exhibitionSettings: this.exhibitionSettings$,
      isTicketOrLegitimationSelectedStep: this.isTicketOrLegitimationSelectedStep$,
      isLogoutProcessing: this.isLogoutProcessing$,
      latestInitializedConteoLoginExhibitionId: this.latestInitializedConteoLoginExhibitionId$
    })
      .pipe(
        first(
          ({
            queryParams,
            isSelectExhibitionPage,
            exhibitionSettings,
            isTicketOrLegitimationSelectedStep,
            isLogoutProcessing,
            latestInitializedConteoLoginExhibitionId
          }) =>
            queryParams.type === SocialLoginQueryParamsLoginType.ConteoLogin &&
            !isSelectExhibitionPage &&
            exhibitionSettings?.initiateConteoLogin &&
            isTicketOrLegitimationSelectedStep &&
            !isLogoutProcessing &&
            exhibitionSettings.eventId === latestInitializedConteoLoginExhibitionId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ queryParams }) => {
        const { voucher, userId, authToken, firstLogin, error } = queryParams;

        const isLoginError = !isNil(error);
        const removeQueryParams = isEmpty(voucher) || isLoginError;

        if (removeQueryParams) {
          this.router.navigate([], { queryParams: {} });
        }

        if (isLoginError) {
          this.statusBarService.setStatus(error, 'error');

          this.store.dispatch(new SetReinitializeConteoLogin(true));
        }

        if (!isNil(userId) && !isNil(authToken)) {
          const isFirstLogin = firstLogin === SocialLoginQueryParamsFirstLogin.True;

          this.loginService.loginAndRedirect(+userId, authToken, isFirstLogin, LoginType.Conteo);
        }
      });
  }

  private setReinitializeConteoLoginOnLogoutProcessingSubscription() {
    return combineLatest({
      profile: this.profile$.pipe(pairwise()),
      isLogoutProcessing: this.isLogoutProcessing$
    })
      .pipe(
        first(
          ({ profile: [_, loggedOutProfile], isLogoutProcessing }) => isNil(loggedOutProfile) && isLogoutProcessing
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.store.dispatch(new SetReinitializeConteoLogin(true)));
  }

  private reinitializeConteoLoginOnPageReloadSubscription() {
    return combineLatest({
      isSelectExhibitionPage: this.isSelectExhibitionPage$,
      isLogoutProcessing: this.isLogoutProcessing$
    })
      .pipe(
        first(),
        filter(({ isSelectExhibitionPage, isLogoutProcessing }) => !isSelectExhibitionPage && !isLogoutProcessing),
        withLatestFrom(
          this.store.select(selectReinitializeConteoLogin),
          this.latestInitializedConteoLoginExhibitionId$
        ),
        filter(
          ([_, reinitializeConteoLogin, latestInitializedConteoLoginExhibitionId]) =>
            reinitializeConteoLogin && !isNil(latestInitializedConteoLoginExhibitionId)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([_, __, latestInitializedConteoLoginExhibitionId]) =>
        this.conteoLogin(latestInitializedConteoLoginExhibitionId)
      );
  }

  private resetConteoLoginOnEventSelectionPageSubscription() {
    return combineLatest({
      isSelectExhibitionPage: this.isSelectExhibitionPage$,
      isLogoutProcessing: this.isLogoutProcessing$,
      latestInitializedConteoLoginExhibitionId: this.latestInitializedConteoLoginExhibitionId$
    })
      .pipe(
        first(
          ({ isSelectExhibitionPage, isLogoutProcessing, latestInitializedConteoLoginExhibitionId }) =>
            isSelectExhibitionPage && !isLogoutProcessing && !isNil(latestInitializedConteoLoginExhibitionId)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.resetInitializeAndReinitializeConteoLogin());
  }

  private resetConteoLoginAfterExhibitionSelectLoginSubscription() {
    return combineLatest({
      isFirstLoadDone: this.isFirstLoadDone$,
      selectedExhibitionId: this.selectedExhibitionId$,
      latestInitializedConteoLoginExhibitionId: this.latestInitializedConteoLoginExhibitionId$
    })
      .pipe(
        first(
          ({ isFirstLoadDone, selectedExhibitionId, latestInitializedConteoLoginExhibitionId }) =>
            isFirstLoadDone &&
            !isNil(selectedExhibitionId) &&
            !isNil(latestInitializedConteoLoginExhibitionId) &&
            selectedExhibitionId !== latestInitializedConteoLoginExhibitionId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.resetInitializeAndReinitializeConteoLogin();

        this.store.dispatch(new SetLatestInitializedConteoLoginExhibitionId(null));
      });
  }

  private resetConteoLoginOnSelectSameExhibitionWithoutLoggedInConteoUserSubscription() {
    return combineLatest({
      isFirstLoadDone: this.isFirstLoadDone$,
      selectedExhibitionId: this.selectedExhibitionId$,
      isLogoutProcessing: this.isLogoutProcessing$,
      latestInitializedConteoLoginExhibitionId: this.latestInitializedConteoLoginExhibitionId$,
      loginType: this.store.select(selectLoginType)
    })
      .pipe(
        first(
          ({
            isFirstLoadDone,
            selectedExhibitionId,
            isLogoutProcessing,
            latestInitializedConteoLoginExhibitionId,
            loginType
          }) =>
            isFirstLoadDone &&
            !isNil(selectedExhibitionId) &&
            !isLogoutProcessing &&
            selectedExhibitionId === latestInitializedConteoLoginExhibitionId &&
            loginType !== LoginType.Conteo
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.resetInitializeAndReinitializeConteoLogin();

        this.store.dispatch(new SetLatestInitializedConteoLoginExhibitionId(null));
      });
  }

  private resetConteoLoginInitializeAndReinitializeAfterUserLoginSubscription() {
    return this.profile$
      .pipe(
        pairwise(),
        first(([profileBeforeLogin, profileAfterLogin]) => isNil(profileBeforeLogin) && !isNil(profileAfterLogin)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.resetInitializeAndReinitializeConteoLogin());
  }

  private conteoLogin(eventId: number) {
    this.resetInitializeAndReinitializeConteoLogin();

    this.store.dispatch(new SetLatestInitializedConteoLoginExhibitionId(eventId));

    const returnUrl = window.location.origin + this.router.url;

    this.loginService.redirectToLoginProvider(
      `${environment.protocol}${environment.webApiUrl}/user/external-login/conteo-login?eventId=${eventId}&returnUrl=${returnUrl}`
    );
  }

  private resetInitializeAndReinitializeConteoLogin() {
    this.store.dispatch(new SetInitializeConteoLogin(false));
    this.store.dispatch(new SetReinitializeConteoLogin(false));
  }
}
